import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';

const MiniappsSection = ({ onUpdate, boostInput = false }) => {
  const { fetchCachedList } = useBuild();
  const [currentMiniapp, setCurrentMiniapp] = useState('');
  const [boost, setBoost] = useState(1);
  const [miniappOptions, setMiniappOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch miniapp category options on component mount
  useEffect(() => {
    const fetchMiniappOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const miniappsData = await fetchCachedList('/search/frequent_values/farcaster-items/miniapp_categories');
        setMiniappOptions(miniappsData);
      } catch (err) {
        console.error('Failed to fetch miniapp category options:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMiniappOptions();
  }, [fetchCachedList]);

  const handleMiniappAction = (actionType) => {
    if (!currentMiniapp) return;
    
    const updateData = {
      action: actionType,
      filter: 'terms',
      field: 'miniapp_categories',
      value: [currentMiniapp]
    };
    
    // Add boost value if boostInput is enabled
    if (boostInput) {
      updateData.boost = boost;
    }
    
    onUpdate(updateData);
    setCurrentMiniapp(''); // Reset after adding
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">
          Failed to load miniapp categories: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Miniapp Category</InputLabel>
        <Select
          value={currentMiniapp}
          label="Miniapp Category"
          onChange={(e) => setCurrentMiniapp(e.target.value)}
          disabled={loading}
        >
          {miniappOptions.map((miniapp) => (
            <MenuItem key={miniapp.id} value={miniapp.id}>
              {miniapp.label || miniapp.id} ({miniapp.count})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
          inputProps={{ min: 0, step: 0.1 }}
          disabled={loading}
        />
      )}
      
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleMiniappAction('include')}
          disabled={!currentMiniapp || loading}
        >
          <AddIcon />
        </Button>
        
        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleMiniappAction('boost')}
            disabled={!currentMiniapp || loading}
          >
            <TrendingUpIcon />
          </Button>
        )}
        
        <Button
          variant="contained"
          color="error"
          onClick={() => handleMiniappAction('exclude')}
          disabled={!currentMiniapp || loading}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default MiniappsSection;

